import { useState } from "react";
import { CheckCircle2, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FadeUp, SectionHeading } from "../components/motion";
import { Honeypot, useGuard, submitForm } from "../components/forms";

const EMPTY = { name: "", organization: "", email: "", message: "" };

export function SponsorDialog({ open, onClose }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const guard = useGuard();
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.organization.trim() || !form.email.trim() || !form.message.trim()) {
      setError("Please complete all fields.");
      return;
    }
    setBusy(true);
    const res = await submitForm("/interest/sponsorship", {
      ...form,
      started_at: guard.startedAt,
      website: guard.honeypot,
    });
    setBusy(false);
    if (res.ok) setDone(true);
    else setError(res.error);
  };

  const close = () => {
    onClose();
    setTimeout(() => {
      setForm(EMPTY);
      setDone(false);
      setError("");
    }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? close() : null)}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto border-navy/15 bg-cream p-7 sm:p-9" data-testid="sponsor-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl font-light text-navy">Sponsorship Inquiry</DialogTitle>
          <DialogDescription className="text-sm leading-relaxed text-navy/65">
            Tell us about your organization and how you might support chaplain renewal at sea.
          </DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="mt-6 flex flex-col items-start gap-3 border border-gold/50 bg-parchment p-6" data-testid="sponsor-success">
            <CheckCircle2 size={26} strokeWidth={1.5} className="text-gold" />
            <p className="font-serif text-xl text-navy">Thank you for your inquiry.</p>
            <p className="text-sm leading-relaxed text-navy/70">
              We welcome the conversation and will be in touch.
            </p>
            <button onClick={close} data-testid="sponsor-success-close" className="mt-2 border border-navy/25 px-5 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-navy hover:border-gold hover:text-gold">
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} noValidate className="mt-6 grid gap-4" data-testid="sponsor-form">
            <Honeypot value={guard.honeypot} onChange={guard.setHoneypot} />
            <div>
              <label htmlFor="sp-name" className="field-label text-navy/70">Name *</label>
              <input id="sp-name" required data-testid="sponsor-name-input" className="field-input mt-1.5" value={form.name} onChange={set("name")} />
            </div>
            <div>
              <label htmlFor="sp-org" className="field-label text-navy/70">Organization *</label>
              <input id="sp-org" required data-testid="sponsor-org-input" className="field-input mt-1.5" value={form.organization} onChange={set("organization")} />
            </div>
            <div>
              <label htmlFor="sp-email" className="field-label text-navy/70">Email *</label>
              <input id="sp-email" type="email" required data-testid="sponsor-email-input" className="field-input mt-1.5" value={form.email} onChange={set("email")} />
            </div>
            <div>
              <label htmlFor="sp-msg" className="field-label text-navy/70">Message *</label>
              <textarea id="sp-msg" required rows={4} data-testid="sponsor-message-input" className="field-input mt-1.5 resize-y" value={form.message} onChange={set("message")} placeholder="How would you like to support chaplain renewal?" />
            </div>
            {error && (
              <p role="alert" data-testid="sponsor-error" className="border border-red-800/30 bg-red-50 px-4 py-3 text-sm text-red-900">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="sponsor-submit-btn"
              className="mt-1 inline-flex items-center justify-center bg-navy px-7 py-3.5 font-mono text-[12px] uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-slate2 disabled:opacity-60"
            >
              {busy ? "Sending..." : "Send Inquiry"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Sponsors() {
  const [open, setOpen] = useState(false);
  return (
    <section id="sponsors" data-testid="section-sponsors" className="bg-cream py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading eyebrow="Sponsors and Partners" titleLines={["Sponsors and", "Partners"]} tone="light-bg" />
        <div className="mt-14 grid gap-6 lg:grid-cols-12">
          <FadeUp className="lg:col-span-7 h-full">
            <div className="flex h-full flex-col justify-between border border-gold/45 bg-parchment p-8 sm:p-10" data-testid="sponsor-ccchaps-card">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-gold">Inaugural Sponsor</p>
                <h3 className="mt-5 font-serif text-3xl font-light text-navy sm:text-4xl">
                  California Corporate Chaplains
                </h3>
                <p className="mt-4 max-w-md text-sm leading-relaxed text-navy/70">
                  Chaplain at Sea is an independent initiative. California Corporate Chaplains
                  (CCCHAPS) participates as a sponsor.
                </p>
              </div>
              <a
                href="https://ccchaps.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="ccchaps-link"
                className="mt-8 inline-flex w-max items-center gap-2 border-b border-gold pb-1 font-mono text-[12px] uppercase tracking-[0.2em] text-ocean transition-colors hover:text-gold"
              >
                ccchaps.com
                <ExternalLink size={13} strokeWidth={1.5} />
              </a>
            </div>
          </FadeUp>
          <FadeUp delay={0.15} className="lg:col-span-5 h-full">
            <div className="flex h-full flex-col justify-between border border-navy/12 bg-white/60 p-8 sm:p-10" data-testid="sponsor-inquiry-card">
              <p className="text-base leading-relaxed text-navy/80">
                Interested in supporting chaplain renewal? We welcome conversations with
                organizations whose services and values support the purpose of Chaplain at Sea.
              </p>
              <button
                data-testid="open-sponsor-modal-btn"
                onClick={() => setOpen(true)}
                className="mt-8 inline-flex items-center justify-center bg-navy px-7 py-3.5 font-mono text-[12px] uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-slate2"
              >
                Inquire About Sponsorship
              </button>
            </div>
          </FadeUp>
        </div>
      </div>
      <SponsorDialog open={open} onClose={() => setOpen(false)} />
    </section>
  );
}
